import { useEffect, useState } from "react";
import QRCode from "qrcode";

interface Props {
  url: string;
  /** Used for the downloaded PNG's filename. */
  filename: string;
  heading: string;
  blurb: string;
}

/**
 * The permanent link to whatever is currently selected, as both text and a QR
 * code. Used for stations (site signage) and for waterbody groups (a lake
 * association's own page or newsletter).
 *
 * The QR encodes exactly the URL shown, so what gets printed and what gets
 * copied can never drift apart.
 */
export default function ShareCard({ url, filename, heading, blurb }: Props) {
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    QRCode.toDataURL(url, {
      margin: 1,
      width: 320,
      color: { dark: "#082948", light: "#FFFFFF" },
    })
      .then(setQr)
      .catch(() => setQr(null));
  }, [url]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard unavailable (insecure context, or denied) — the link is
      // visible and selectable anyway.
    }
  }

  return (
    <section className="share">
      <h3>{heading}</h3>
      <p>{blurb}</p>
      <div className="share-body">
        {qr && <img className="qr" src={qr} alt={`QR code linking to ${url}`} />}
        <div className="share-side">
          <div className="share-url" title={url}>{url}</div>
          <div className="share-actions">
            <button className="btn sm" onClick={copy}>{copied ? "Copied" : "Copy link"}</button>
            {qr && (
              <a className="btn ghost sm" href={qr} download={`${filename}-qr.png`}>
                Download QR
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
